/**
 * `fetch`, but it gives up.
 *
 * ## The failure this exists to stop
 *
 * `fetch` has **no timeout of its own**. A hotel's captive portal — the single
 * most common network a Foyer panel ever sits behind — completes the TCP
 * handshake and then never answers. The request does not fail; it simply never
 * settles, and neither does the promise.
 *
 * 🚨 That is worse than an error for every polling hook in `src/tv/hooks/`.
 * They are all shaped like this:
 *
 * ```ts
 * try   { const res = await fetch(url); …; schedule(POLL_MS) }
 * catch { failures += 1; schedule(backoff(failures)) }
 * ```
 *
 * If the fetch never settles, the `catch` never runs — so **the reschedule
 * never runs either**. The loop does not back off and retry slowly; it stops
 * dead, for the lifetime of the mount, with no error anywhere. A guest watches
 * a spinner, or a stale guide, until somebody reboots the panel. It was
 * described in `useTvCatalogue` as "the poll and backoff already bound the
 * wait" — they do not, and cannot: a backoff only ever schedules the attempt
 * *after* the current one finishes.
 *
 * Bounding the request converts a permanent hang into an ordinary failure,
 * which every one of these hooks already knows how to handle: count it, back
 * off, keep the last good data, try again.
 *
 * ## Why the timeout is generous
 *
 * These panels are on hotel Wi-Fi, sometimes several NAT hops from the router,
 * and a slow answer is still a useful answer. The bound is here to catch the
 * *never* case, not to police latency — so it is set well above any plausible
 * real response rather than tuned tight.
 */

/** Long enough that a slow-but-working network still wins. */
export const FETCH_TIMEOUT_MS = 8_000;

/**
 * A signal that aborts after `ms`, plus the cleanup that cancels the timer.
 *
 * Degrades to an unbounded request where `AbortController` is missing rather
 * than throwing: some of the older MediaSuite WebViews we target predate it,
 * and on those the old behaviour is still better than a crash.
 */
export function deadline(ms: number = FETCH_TIMEOUT_MS): {
  signal?: AbortSignal;
  done: () => void;
} {
  if (typeof AbortController !== "function") return { done: () => {} };
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, done: () => clearTimeout(timer) };
}

/**
 * `fetch(input, init)` that rejects instead of hanging.
 *
 * An abort surfaces as a rejection, which is exactly what the callers' existing
 * `catch` branches want — no caller needs to learn a new failure shape.
 *
 * ⚠️ Callers that pass their own `signal` keep it; this only adds a bound when
 * there is none, so an explicit caller-owned cancellation is never overridden.
 */
export async function fetchBounded(
  input: string,
  init: RequestInit = {},
  timeoutMs: number = FETCH_TIMEOUT_MS
): Promise<Response> {
  if (init.signal) return fetch(input, init);
  const { signal, done } = deadline(timeoutMs);
  try {
    return await fetch(input, signal ? { ...init, signal } : init);
  } finally {
    done();
  }
}
